import { DateTime } from 'luxon';

/**
 * Composable for formatting dates, times, and locale strings used in movement components.
 * Centralizes all date/time formatting logic to reduce code duplication.
 */
export function useMovementFormatting() {
    /**
     * Formats locale strings by removing country suffix
     * @param {string} locale - Locale string (e.g., "London, UK")
     * @returns {string} - City only (e.g., "London")
     */
    const formatLocale = (locale) => {
        if (!locale) {
            return '';
        }
        const pos = locale.indexOf(',');
        return pos !== -1 ? locale.substring(0, pos) : locale;
    };

    /**
     * Formats date string using Luxon with customizable format
     * @param {string} dateString - ISO date string
     * @param {string} format - Luxon format string (default: 'd LLL yyyy')
     * @returns {string} - Formatted date (e.g., "4 Dec 2025")
     */
    const formatDate = (dateString, format = 'd LLL yyyy') => {
        if (!dateString) {
            console.error('No or incorrect date string provided');
            return '';
        }
        let date = DateTime.fromISO(dateString);
        if (!date.isValid) {
            console.error('Invalid date string provided:', dateString);
            return '';
        }
        return date.toFormat(format);
    };

    /**
     * Formats date in short format with day name
     * @param {string} dateString - ISO date string
     * @returns {string} - Formatted date (e.g., "4 Dec 2025")
     */
    const formatDateShort = (dateString) => {
        const date = new Date(dateString);
        return new Intl.DateTimeFormat('en-GB', {
            day: 'numeric',
            month: 'short',
            year: 'numeric'
        }).format(date);
    };

    /**
     * Formats date in long format with weekday abbreviation
     * @param {string} dateString - ISO date string
     * @returns {string} - Formatted date (e.g., "Mon 4 Dec 25")
     */
    const formatDateLong = (dateString) => {
        const date = new Date(dateString);
        return new Intl.DateTimeFormat('en-GB', {
            weekday: 'short',
            day: 'numeric',
            month: 'short',
            year: '2-digit'
        }).format(date).replace(/(\d)(st|nd|rd|th)/, '$1$2');
    };

    /**
     * Creates a relative time string showing time elapsed since creation
     * @param {string} dateString - ISO date string
     * @returns {string} - Relative time (e.g., "Created 2 hours 15 minutes 30 seconds ago")
     */
    const formatRelativeTime = (dateString) => {
        const createdMs = new Date(dateString).getTime();
        let seconds = Math.floor((Date.now() - createdMs) / 1000);
        const output = [];
        const units = {
            year: 31536000,
            month: 2592000,
            day: 86400,
            hour: 3600,
            minute: 60
        };

        Object.keys(units).forEach((unit) => {
            let interval = Math.floor(seconds / units[unit]);
            if (interval >= 1) {
                output.push(`${interval} ${unit}${interval !== 1 ? 's' : ''}`);
                seconds -= (interval * units[unit]);
            }
        });

        if (output.length > 0) {
            output.push('');
        }

        return `Created ${output.join(' ')}${Math.floor(seconds)} seconds ago`;
    };

    return {
        formatLocale,
        formatDate,
        formatDateShort,
        formatDateLong,
        formatRelativeTime
    };
}
